import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Alert,
    CircularProgress,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Badge,
    Snackbar,
    SnackbarContent,
    Switch,
    Autocomplete,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    CloudDownload as ScrapeIcon,
    Analytics as AnalyticsIcon,
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
    GetApp as ExportIcon,
    PlayArrow as PlayIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    Queue as QueueIcon,
    ExpandMore as ExpandMoreIcon,
    Clear as ClearIcon,
    AttachMoney as PricingIcon,
    Description as DescriptionIcon,
    Cancel as CancelIcon,
    Upload as UploadIcon,
    SmartToy as KimiIcon,
    Image as ImageIcon,
    Map as MapIcon,
    Assignment as ReportIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import {
    analyzeWithKimi,
    analyzeFacilityWithKimi,
    batchAnalyzeWithKimi // Import the new batch function
} from '../api/api';

// API base URL
const API_BASE = (process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`);

const FacilitiesManager = () => {
    const { state, apiActions, dispatch } = useApp();
    const { facilities, facilitiesLoading, analysisTypes, runningAnalyses, runningScraping, queuedScraping, scrapingProgress, systemStatus } = state;

    // Local state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [analysisFilter, setAnalysisFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState('id');
    const [order, setOrder] = useState('asc');
    const [selectedFacilities, setSelectedFacilities] = useState([]);

    // Add automation state
    const [autoScrapeEnabled, setAutoScrapeEnabled] = useState(false);
    const [autoAnalysisEnabled, setAutoAnalysisEnabled] = useState(false);
    const [automationLoading, setAutomationLoading] = useState(false);

    // Analysis progress state
    const [analysisProgress, setAnalysisProgress] = useState({});

    // New state for facility analysis summaries
    const [facilityAnalysisSummaries, setFacilityAnalysisSummaries] = useState({});
    const [analysisSummariesLoading, setAnalysisSummariesLoading] = useState(false);

    // Dialog states
    const [showScrapeDialog, setScrapeDialog] = useState(false);
    const [showAnalysisDialog, setAnalysisDialog] = useState(false);
    const [showBulkAnalysisDialog, setBulkAnalysisDialog] = useState(false);
    const [showDetailsDialog, setDetailsDialog] = useState(false);
    const [showSendToKiddieDialog, setSendToKiddieDialog] = useState(false);
    const [showKimiAnalysisDialog, setKimiAnalysisDialog] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [facilityDetails, setFacilityDetails] = useState(null);

    // Analysis dialog state
    const [selectedAnalysisTypes, setSelectedAnalysisTypes] = useState([]);
    const [forceReanalysis, setForceReanalysis] = useState(false);
    const [analysisLoading, setAnalysisLoading] = useState(false);

    // Scraping dialog state
    const [scrapeLoading, setScrapeLoading] = useState(false);

    // Bulk analysis dialog state
    const [bulkAnalysisLoading, setBulkAnalysisLoading] = useState(false);

    // Send to Kiddie dialog state
    const [sendToKiddieLoading, setSendToKiddieLoading] = useState(false);
    // const [includePricing, setIncludePricing] = useState(true);  // COMMENTED OUT - No pricing data

    // Send inspection reports to Kiddie dialog state
    const [sendInspectionReportsToKiddieDialog, setSendInspectionReportsToKiddieDialog] = useState(false);
    const [sendInspectionReportsToKiddieLoading, setSendInspectionReportsToKiddieLoading] = useState(false);

    // Kimi analysis dialog state
    const [kimiAnalysisLoading, setKimiAnalysisLoading] = useState(false);
    const [kimiFormData, setKimiFormData] = useState({
        facility_name: '',
        facility_location: '',
        facility_type: 'KDV',
        website: ''
    });
    const [kimiAnalysisResult, setKimiAnalysisResult] = useState(null);

    // Google Images search state
    const [googleImagesLoading, setGoogleImagesLoading] = useState(false);

    // Google Data search state
    const [googleDataLoading, setGoogleDataLoading] = useState(false);

    // Error/success notifications
    const [notification, setNotification] = useState(null);

    // Request deduplication to prevent API spam
    const [ongoingRequests, setOngoingRequests] = useState(new Set());
    const [facilityDetailsCache, setFacilityDetailsCache] = useState(new Map());

    // Only refresh summaries when analyses actually complete, not every 5 seconds
    const [lastAnalysisCount, setLastAnalysisCount] = useState(0);
    useEffect(() => {
        const currentCount = Object.keys(analysisProgress).length;
        // Only refresh if we went from having analyses to no analyses (completion)
        if (lastAnalysisCount > 0 && currentCount === 0) {
            console.log('✅ Analyses completed, refreshing summaries once');
            fetchFacilityAnalysisSummaries();
        }
        setLastAnalysisCount(currentCount);
    }, [analysisProgress]); // Removed fetchFacilityAnalysisSummaries dependency

    // Fetch analysis summaries when component mounts (once only)
    useEffect(() => {
        console.log('🚀 Component mounted, initializing analysis summaries...');
        fetchFacilityAnalysisSummaries();
    }, []); // Empty dependency array = run once on mount

    // Fetch automation status on component mount
    useEffect(() => {
        const fetchAutomationStatus = async () => {
            try {
                const status = await apiActions.getAutomationStatus();
                if (status.success) {
                    setAutoScrapeEnabled(status.auto_scrape_enabled);
                    setAutoAnalysisEnabled(status.auto_analysis_enabled);
                }
            } catch (error) {
                console.error('Failed to fetch automation status:', error);
            }
        };

        fetchAutomationStatus();
    }, []);

    // Analysis summaries fetching function - OPTIMIZED: Uses bulk endpoint instead of N+1 requests
    const fetchFacilityAnalysisSummaries = useCallback(async () => {
        setAnalysisSummariesLoading(true);
        try {
            console.log('📊 Fetching analysis summaries using BULK endpoint...');

            // Use the optimized bulk endpoint through API service
            const data = await apiActions.getAnalysisSummaries();

            if (data.success && data.summaries) {
                console.log(`✅ Bulk loaded ${data.count} analysis summaries in single request`);
                setFacilityAnalysisSummaries(data.summaries);
            } else {
                console.warn('⚠️ Bulk summaries response not successful:', data);
                setFacilityAnalysisSummaries({});
            }
        } catch (error) {
            console.error('💥 Error fetching bulk analysis summaries:', error);
            setFacilityAnalysisSummaries({});
        }
        setAnalysisSummariesLoading(false);
    }, [apiActions]);

    // Fetch analysis progress
    const fetchAnalysisProgress = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/analysis/status`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setAnalysisProgress(data.status.running_analyses || {});
                }
            }
        } catch (error) {
            console.error('Failed to fetch analysis progress:', error);
        }
    }, []);

    // Helper functions
    const getAnalysisStatusForFacility = (facilityId) => {
        const facilityAnalyses = Object.keys(analysisProgress).filter(key =>
            key.startsWith(`${facilityId}_`)
        );
        return facilityAnalyses.length;
    };

    const getAnalysisProgressForFacility = (facilityId) => {
        const facilityAnalyses = Object.entries(analysisProgress).filter(([key, _]) =>
            key.startsWith(`${facilityId}_`)
        );
        return facilityAnalyses;
    };

    const getScrapingStatusForFacility = (facility) => {
        // Check both facility.id and facility.lrk_id since API returns LRK IDs
        const isRunning = runningScraping.includes(facility.id) || runningScraping.includes(facility.lrk_id);
        if (isRunning) {
            console.log(`🔍 Facility ${facility.lrk_id} (${facility.name}) is currently being scraped`);
        }
        return isRunning;
    };

    const getQueuedStatusForFacility = (facility) => {
        // Check if facility is queued for scraping
        return queuedScraping.includes(facility.id) || queuedScraping.includes(facility.lrk_id);
    };

    const isProcessing = (facility) => {
        return getAnalysisStatusForFacility(facility.id) > 0 || getScrapingStatusForFacility(facility) || getQueuedStatusForFacility(facility);
    };

    const getFacilityAnalysisCount = (facility) => {
        // Get running analyses count
        const runningCount = getAnalysisStatusForFacility(facility.id);

        // Get completed analyses count from analysis summary
        const summary = facilityAnalysisSummaries[facility.id];
        let completedCount = 0;

        if (summary && summary.analyses) {
            completedCount = Object.values(summary.analyses).filter(analysis =>
                analysis.available === true || analysis.status === 'completed'
            ).length;
        }

        // Debug logging for specific facility
        if (facility.id === '04079639-ddd9-4bc2-b91c-312edc9705d4') {
            console.log(`🔍 Debug facility ${facility.id}:`, {
                runningCount,
                completedCount,
                totalCount: runningCount + completedCount,
                summary,
                facilityAnalysisSummariesKeys: Object.keys(facilityAnalysisSummaries),
                facilityAnalysisSummariesCount: Object.keys(facilityAnalysisSummaries).length,
                hasThisFacility: !!facilityAnalysisSummaries[facility.id],
                analysisSummariesLoading
            });
        }

        return runningCount + completedCount;
    };

    const getStatusChip = (facility) => {
        const runningAnalysesForFacility = getAnalysisProgressForFacility(facility.id);
        const isScrapingNow = getScrapingStatusForFacility(facility);
        const isQueuedForScraping = getQueuedStatusForFacility(facility);

        if (isScrapingNow) {
            // Get detailed progress for this facility
            const progress = scrapingProgress[facility.lrk_id] || scrapingProgress[facility.id];

            if (progress) {
                return (
                    <Box sx={{ minWidth: 120 }}>
                        <Tooltip title={progress.status_message}>
                            <Box>
                                <Chip
                                    icon={<ScheduleIcon />}
                                    label={`${progress.current_step} (${progress.progress_percent}%)`}
                                    color="warning"
                                    size="small"
                                    sx={{ mb: 0.5, minWidth: 120 }}
                                />
                                <LinearProgress
                                    variant="determinate"
                                    value={progress.progress_percent}
                                    sx={{ height: 4, borderRadius: 2 }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    {progress.status_message}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Box>
                );
            } else {
                return (
                    <Chip icon={<ScheduleIcon />} label="Scraping" color="warning" size="small" />
                );
            }
        }

        if (isQueuedForScraping) {
            return (
                <Chip icon={<QueueIcon />} label="Queued" color="info" size="small" />
            );
        }

        if (runningAnalysesForFacility.length > 0) {
            // Show detailed analysis progress
            const [analysisKey, progressData] = runningAnalysesForFacility[0]; // Show first running analysis
            const analysisType = analysisKey.split('_').pop(); // Extract analysis type from key

            return (
                <Box sx={{ minWidth: 140 }}>
                    <Tooltip title={`${analysisType}: ${progressData.status_message}`}>
                        <Box>
                            <Chip
                                icon={analysisType === 'pricing' ? <PricingIcon /> : <DescriptionIcon />}
                                label={`${analysisType} (${progressData.progress_percent}%)`}
                                color="info"
                                size="small"
                                sx={{ mb: 0.5, minWidth: 140 }}
                            />
                            <LinearProgress
                                variant="determinate"
                                value={progressData.progress_percent}
                                sx={{ height: 4, borderRadius: 2 }}
                                color="info"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {progressData.current_step}: {progressData.status_message}
                            </Typography>
                            {runningAnalysesForFacility.length > 1 && (
                                <Badge badgeContent={runningAnalysesForFacility.length} color="primary" sx={{ ml: 1 }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>
            );
        }

        if (facility.scraped_at) {
            // Check if this facility has URL analysis failures
            const facilitySummary = facilityAnalysisSummaries[facility.id];
            if (facilitySummary && facilitySummary.url_analysis_status === 'failed') {
                return (
                    <Tooltip title={`URL analysis failed during scraping (${facilitySummary.url_analysis_errors?.length || 0} errors). Analysis is blocked. Re-scrape to retry.`}>
                        <Chip
                            icon={<ErrorIcon />}
                            label="URL Analysis Failed"
                            color="error"
                            size="small"
                            sx={{ backgroundColor: '#ffebee', color: '#c62828' }}
                        />
                    </Tooltip>
                );
            }

            // Check if this facility has empty website content
            if (facilitySummary && facilitySummary.website_content_empty) {
                return (
                    <Tooltip title="Scraping failed - no website content captured. Re-scrape to retry.">
                        <Chip
                            icon={<ErrorIcon />}
                            label="Scraping Failed"
                            color="error"
                            size="small"
                            sx={{ backgroundColor: '#ffebee', color: '#c62828' }}
                        />
                    </Tooltip>
                );
            }

            return <Chip icon={<CheckIcon />} label="Scraped" color="success" size="small" />;
        }

        return <Chip icon={<ErrorIcon />} label="Not Scraped" color="default" size="small" />;
    };

    const handleQuickAnalysis = async (facility, analysisType, event = null) => {
        // Check if Ctrl/Cmd key was pressed to force re-analysis
        const forceReanalysis = event && (event.ctrlKey || event.metaKey);

        // Debug logging
        console.log('🔍 handleQuickAnalysis called:', {
            facility: facility.id,
            analysisType,
            event: event ? 'present' : 'null',
            ctrlKey: event?.ctrlKey,
            metaKey: event?.metaKey,
            forceReanalysis
        });

        try {
            const response = await fetch(`${API_BASE}/facilities/${facility.id}/analyze`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    analysis_type: analysisType,
                    force_reanalysis: forceReanalysis
                })
            });

            const result = await response.json();

            if (result.success) {
                // Start polling for this specific analysis
                fetchAnalysisProgress();

                // Use the API's message and status for better feedback
                const statusText = result.status === 'queued' ? 'queued' : 'started';
                const apiMessage = result.message || `${analysisType} analysis ${statusText}`;

                setNotification({
                    type: result.status === 'queued' ? 'info' : 'success',
                    message: `${apiMessage} for ${facility.name || facility.id}`
                });
            } else {
                // Handle specific error cases
                if (result.error_details && result.error_details.status === 'url_analysis_failed') {
                    // URL analysis failed during scraping - show specific message and re-scrape option
                    setNotification({
                        type: 'warning',
                        message: `Analysis blocked: URL analysis failed during scraping (${result.error_details.url_analysis_errors?.length || 0} errors)`,
                        action: {
                            label: 'Re-scrape Facility',
                            onClick: () => handleRescrapeFromError(facility.id)
                        }
                    });
                } else if (result.error && typeof result.error === 'object') {
                    const errorObj = result.error;
                    if (errorObj.status === 'no_content') {
                        setNotification({
                            type: 'warning',
                            message: `No suitable content found for ${analysisType} analysis`,
                            action: {
                                label: 'Re-scrape Facility',
                                onClick: () => handleRescrapeFromError(facility.id)
                            }
                        });
                    } else if (errorObj.status === 'no_pricing_content') {
                        setNotification({
                            type: 'info',
                            message: 'No pricing information found - many facilities only share pricing on request'
                        });
                    } else {
                        setNotification({
                            type: 'error',
                            message: errorObj.message || `${analysisType} analysis failed`
                        });
                    }
                } else {
                    setNotification({
                        type: 'error',
                        message: result.error || result.message || `Failed to start ${analysisType} analysis`
                    });
                }
            }
        } catch (e) {
            console.error(`Failed to start ${analysisType} analysis:`, e);
            setNotification({
                type: 'error',
                message: `Failed to start ${analysisType} analysis: ${e.message}`
            });
        }
    };

    const handleQuickDescriptionAnalysis = async (facility, enableOptimization, event = null) => {
        // Check if Ctrl/Cmd key was pressed to force re-analysis
        const forceReanalysis = event && (event.ctrlKey || event.metaKey);
        const analysisMode = enableOptimization ? 'optimized' : 'full content';

        // Debug logging
        console.log('🔍 handleQuickDescriptionAnalysis called:', {
            facility: facility.id,
            enableOptimization,
            analysisMode,
            event: event ? 'present' : 'null',
            ctrlKey: event?.ctrlKey,
            metaKey: event?.metaKey,
            forceReanalysis
        });

        try {
            const response = await fetch(`${API_BASE}/facilities/${facility.id}/analyze`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    analysis_type: 'description',
                    force_reanalysis: forceReanalysis,
                    enable_optimization: enableOptimization
                })
            });

            const result = await response.json();

            if (result.success) {
                // Start polling for this specific analysis
                fetchAnalysisProgress();

                // Use the API's message and status for better feedback
                const statusText = result.status === 'queued' ? 'queued' : 'started';
                const apiMessage = result.message || `Description analysis (${analysisMode}) ${statusText}`;

                setNotification({
                    type: result.status === 'queued' ? 'info' : 'success',
                    message: `${apiMessage} for ${facility.name || facility.id}`
                });
            } else {
                // Handle specific error cases
                if (result.error_details && result.error_details.status === 'url_analysis_failed') {
                    // URL analysis failed during scraping - show specific message and re-scrape option
                    setNotification({
                        type: 'warning',
                        message: `Analysis blocked: URL analysis failed during scraping (${result.error_details.url_analysis_errors?.length || 0} errors)`,
                        action: {
                            label: 'Re-scrape Facility',
                            onClick: () => handleRescrapeFromError(facility.id)
                        }
                    });
                } else if (result.error && typeof result.error === 'object') {
                    const errorObj = result.error;
                    if (errorObj.status === 'no_content') {
                        setNotification({
                            type: 'warning',
                            message: `No suitable content found for description analysis`,
                            action: {
                                label: 'Re-scrape Facility',
                                onClick: () => handleRescrapeFromError(facility.id)
                            }
                        });
                    } else {
                        setNotification({
                            type: 'error',
                            message: errorObj.message || `Description analysis failed`
                        });
                    }
                } else {
                    setNotification({
                        type: 'error',
                        message: result.error || result.message || `Failed to start description analysis`
                    });
                }
            }
        } catch (e) {
            console.error(`Failed to start description analysis:`, e);
            setNotification({
                type: 'error',
                message: `Failed to start description analysis: ${e.message}`
            });
        }
    };

    const handleRescrapeFromError = async (facilityId) => {
        try {
            const result = await apiActions.startScraping([facilityId], null, true);
            if (result.success) {
                setNotification({
                    type: 'success',
                    message: 'Re-scraping started - analysis can be retried once complete'
                });
            }
        } catch (e) {
            setNotification({
                type: 'error',
                message: `Failed to start re-scraping: ${e.message}`
            });
        }
    };

    // Filtering and sorting
    const filteredFacilities = useMemo(() => {
        // Debug: Check if our target facility exists in the facilities array
        const targetFacility = '04079639-ddd9-4bc2-b91c-312edc9705d4';
        const foundFacility = facilities.find(f => f.id === targetFacility);

        if (foundFacility) {
            console.log(`✅ Target facility found in facilities array:`, foundFacility);
        } else {
            console.log(`❌ Target facility NOT found in facilities array. Total facilities: ${facilities.length}`);
        }

        let filtered = facilities.filter((facility) => {
            // Search filter
            const matchesSearch = !searchTerm ||
                facility.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (facility.name && facility.name.toLowerCase().includes(searchTerm.toLowerCase()));

            // Status filter - fix to handle actual data structure
            let matchesStatus = false;
            if (statusFilter === 'all') {
                matchesStatus = true;
            } else if (statusFilter === 'scraped') {
                // Check both status field and scraped_at field, but exclude if website content is empty
                const facilitySummary = facilityAnalysisSummaries[facility.id];
                const hasEmptyWebsiteContent = facilitySummary && facilitySummary.website_content_empty;
                matchesStatus = (facility.status === 'scraped' || facility.scraped_at !== null) && !hasEmptyWebsiteContent;
            } else if (statusFilter === 'not_scraped') {
                // Check both status field and scraped_at field, but exclude if website content is empty (those are scraping_failed)
                const facilitySummary = facilityAnalysisSummaries[facility.id];
                const hasEmptyWebsiteContent = facilitySummary && facilitySummary.website_content_empty;
                matchesStatus = (facility.status !== 'scraped' && facility.scraped_at === null) && !hasEmptyWebsiteContent;
            } else if (statusFilter === 'scraping_failed') {
                // Check for facilities where scraping was attempted but failed (empty website content)
                const facilitySummary = facilityAnalysisSummaries[facility.id];
                matchesStatus = facilitySummary && facilitySummary.website_content_empty;
            } else if (statusFilter === 'url_analysis_failed') {
                // Check for URL analysis failures
                const facilitySummary = facilityAnalysisSummaries[facility.id];
                matchesStatus = facilitySummary && facilitySummary.url_analysis_status === 'failed';
            } else if (statusFilter === 'queued') {
                matchesStatus = getQueuedStatusForFacility(facility);
            } else if (statusFilter === 'processing') {
                matchesStatus = isProcessing(facility);
            }

            // Analysis filter - now properly counts completed analyses
            const analysisCount = getFacilityAnalysisCount(facility);
            let matchesAnalysis = false;

            if (analysisFilter === 'all') {
                matchesAnalysis = true;
            } else if (analysisFilter === 'analyzed') {
                matchesAnalysis = analysisCount > 0;
            } else if (analysisFilter === 'not_analyzed') {
                matchesAnalysis = analysisCount === 0;
            }

            // Debug logging for specific facility
            if (facility.id === '04079639-ddd9-4bc2-b91c-312edc9705d4') {
                console.log(`🔍 Filter debug for ${facility.id}:`, {
                    facility: facility.name,
                    matchesSearch,
                    matchesStatus,
                    matchesAnalysis,
                    analysisCount,
                    searchTerm,
                    statusFilter,
                    analysisFilter,
                    finalMatch: matchesSearch && matchesStatus && matchesAnalysis,
                    facilityAnalysisSummariesLoaded: Object.keys(facilityAnalysisSummaries).length > 0
                });
            }

            return matchesSearch && matchesStatus && matchesAnalysis;
        });

        // Sorting
        filtered.sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            if (orderBy === 'scraped_at') {
                aValue = a.scraped_at ? new Date(a.scraped_at) : new Date(0);
                bValue = b.scraped_at ? new Date(b.scraped_at) : new Date(0);
            } else if (orderBy === 'kimi_confidence') {
                // Extract confidence from facilityAnalysisSummaries
                aValue = facilityAnalysisSummaries[a.id]?.kimi_confidence ?? -1;
                bValue = facilityAnalysisSummaries[b.id]?.kimi_confidence ?? -1;
            }

            if (aValue < bValue) return order === 'asc' ? -1 : 1;
            if (aValue > bValue) return order === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [facilities, searchTerm, statusFilter, analysisFilter, orderBy, order, runningAnalyses, runningScraping, queuedScraping, scrapingProgress, systemStatus, analysisProgress, facilityAnalysisSummaries, analysisSummariesLoading]);

    // Pagination
    const paginatedFacilities = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return filteredFacilities.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredFacilities, page, rowsPerPage]);

    // Event handlers
    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedFacilities(paginatedFacilities.map(f => f.id));
        } else {
            setSelectedFacilities([]);
        }
    };

    const handleSelectFacility = (facilityId) => {
        setSelectedFacilities(prev =>
            prev.includes(facilityId)
                ? prev.filter(id => id !== facilityId)
                : [...prev, facilityId]
        );
    };

    const handleViewDetails = async (facility) => {
        setDetailsDialog(true);
        setSelectedFacility(facility);
        setFacilityDetails(null);

        try {
            const details = await apiActions.getFacilityDetails(facility.id);
            if (details.success) {
                setFacilityDetails(details);
            } else {
                dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: `Failed to load details: ${details.error}` } });
            }
        } catch (error) {
            console.error('Failed to load facility details:', error);
            dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: `Failed to load details: ${error.message}` } });
        }
    };

    const handleRunAnalysis = (facility) => {
        setSelectedFacility(facility);
        setSelectedAnalysisTypes([]);
        setForceReanalysis(false);
        setAnalysisDialog(true);
    };

    const handleStartAnalysis = async () => {
        if (!selectedFacility || selectedAnalysisTypes.length === 0) return;

        setAnalysisLoading(true);
        try {
            let result;
            if (selectedAnalysisTypes.length === 1) {
                result = await apiActions.runAnalysis(
                    selectedFacility.id,
                    selectedAnalysisTypes[0],
                    forceReanalysis
                );
            } else {
                result = await apiActions.runMultipleAnalyses(
                    selectedFacility.id,
                    selectedAnalysisTypes,
                    forceReanalysis
                );
            }

            if (result.success) {
                setAnalysisDialog(false);
                // Start polling for progress
                fetchAnalysisProgress();
            }
        } catch (error) {
            console.error('Analysis failed:', error);
        }
        setAnalysisLoading(false);
    };

    const handleBulkScrape = async () => {
        setScrapeLoading(true);
        try {
            const result = await apiActions.startIndividualScraping(
                selectedFacilities.length > 0 ? selectedFacilities : null,
                true // forceRescrape - always force re-scraping when user clicks scrape
            );

            if (result.success) {
                setScrapeDialog(false);
                setSelectedFacilities([]);
            }
        } catch (error) {
            console.error('Scraping failed:', error);
        }
        setScrapeLoading(false);
    };

    const handleBulkFullAnalysis = async () => {
        if (selectedFacilities.length === 0) return;

        setBulkAnalysisLoading(true);
        try {
            // Get only properly scraped facilities from selection (exclude scraping failed)
            const scrapedFacilities = facilities.filter(f => {
                if (!selectedFacilities.includes(f.id) || !f.scraped_at) {
                    return false;
                }
                // Exclude facilities where scraping failed (empty website content)
                const facilitySummary = facilityAnalysisSummaries[f.id];
                const hasEmptyWebsiteContent = facilitySummary && facilitySummary.website_content_empty;
                return !hasEmptyWebsiteContent;
            });

            if (scrapedFacilities.length === 0) {
                setNotification({
                    type: 'warning',
                    message: 'No properly scraped facilities in selection. Only successfully scraped facilities can be analyzed.'
                });
                setBulkAnalysisLoading(false);
                return;
            }

            // Use individual analysis tasks instead of looping
            const facilityIds = scrapedFacilities.map(f => f.id);
            const result = await apiActions.startIndividualAnalysis(facilityIds, 'description');

            if (result.success) {
                setBulkAnalysisDialog(false);
                setSelectedFacilities([]);

                setNotification({
                    type: 'success',
                    message: `Queued ${result.task_count} individual description analysis tasks`
                });
            }
        } catch (error) {
            console.error('Bulk analysis failed:', error);
            setNotification({
                type: 'error',
                message: `Failed to queue analysis tasks: ${error.message}`
            });
        }
        setBulkAnalysisLoading(false);
    };

    const handleSendToKiddie = async () => {
        setSendToKiddieLoading(true);
        try {
            const result = await apiActions.sendToKiddie(
                selectedFacilities.length > 0 ? selectedFacilities : null,
                // includePricing,  // COMMENTED OUT - No pricing data
                true,  // COMMENTED OUT - No pricing data, keeping parameter for compatibility
                2 // max concurrent
            );

            if (result.success) {
                setSendToKiddieDialog(false);
                setSelectedFacilities([]);
                setNotification({
                    type: 'success',
                    message: `Successfully created ${result.task_count} individual tasks for Kiddie API! Coordinator: ${result.task_id}`
                });
            }
        } catch (error) {
            console.error('Send to Kiddie failed:', error);
            setNotification({
                type: 'error',
                message: `Failed to send data to Kiddie: ${error.message}`
            });
        }
        setSendToKiddieLoading(false);
    };

    const handleSendInspectionReportsToKiddie = async () => {
        setSendInspectionReportsToKiddieLoading(true);
        try {
            const result = await apiActions.sendInspectionReportsToKiddie(
                selectedFacilities.length > 0 ? selectedFacilities : null,
                2 // max concurrent
            );

            if (result.success) {
                setSendInspectionReportsToKiddieDialog(false);
                setSelectedFacilities([]);
                setNotification({
                    type: 'success',
                    message: `Successfully created ${result.task_count} individual inspection report tasks for Kiddie API! Coordinator: ${result.task_id}`
                });
            }
        } catch (error) {
            console.error('Send inspection reports to Kiddie failed:', error);
            setNotification({
                type: 'error',
                message: `Failed to send inspection reports to Kiddie: ${error.message}`
            });
        }
        setSendInspectionReportsToKiddieLoading(false);
    };

    // Kimi Analysis Handler
    const handleKimiAnalysis = async () => {
        if (!kimiFormData.facility_name) {
            setNotification({ message: 'Please fill in facility name', type: 'error' });
            return;
        }

        setKimiAnalysisLoading(true);
        setKimiAnalysisResult(null);

        try {
            const result = await analyzeWithKimi(kimiFormData);
            setKimiAnalysisResult(result);
            setNotification({
                message: `Kimi analysis completed for ${kimiFormData.facility_name}`,
                type: 'success'
            });
        } catch (error) {
            console.error('Error in Kimi analysis:', error);
            setNotification({
                message: `Kimi analysis failed: ${error.message}`,
                type: 'error'
            });
        } finally {
            setKimiAnalysisLoading(false);
        }
    };

    const handleKimiFormChange = (field, value) => {
        setKimiFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleKimiAnalysisFromSelection = async () => {
        if (selectedFacilities.length === 0) {
            setNotification({ message: 'No facilities selected', type: 'error' });
            return;
        }

        setKimiAnalysisLoading(true);

        try {
            // Call the new batch endpoint
            const result = await batchAnalyzeWithKimi(selectedFacilities);

            setNotification({
                message: `✅ ${result.message}`,
                type: 'success'
            });

            // Clear selection after successful queuing
            setSelectedFacilities([]);

        } catch (error) {
            console.error('Error in batch Kimi analysis:', error);
            setNotification({
                message: `❌ Kimi analysis batch failed: ${error.message}`,
                type: 'error'
            });
        } finally {
            setKimiAnalysisLoading(false);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setAnalysisFilter('all');
        setPage(0);
    };

    // Function to clear cached facility details
    const clearFacilityDetailsCache = (facilityId = null) => {
        if (facilityId) {
            setFacilityDetailsCache(prev => {
                const newCache = new Map([...prev]);
                newCache.delete(facilityId);
                return newCache;
            });
        } else {
            setFacilityDetailsCache(new Map());
        }
    };

    // Handle automation toggle changes
    const handleAutoScrapeToggle = async (enabled) => {
        setAutomationLoading(true);
        try {
            const result = enabled
                ? await apiActions.enableAutoScrape()
                : await apiActions.disableAutoScrape();

            if (result.success) {
                setAutoScrapeEnabled(enabled);
            }
        } catch (error) {
            console.error('Failed to toggle auto-scrape:', error);
        }
        setAutomationLoading(false);
    };

    const handleAutoAnalysisToggle = async (enabled) => {
        setAutomationLoading(true);
        try {
            const result = enabled
                ? await apiActions.enableAutoAnalysis()
                : await apiActions.disableAutoAnalysis();

            if (result.success) {
                setAutoAnalysisEnabled(enabled);
            }
        } catch (error) {
            console.error('Failed to toggle auto-analysis:', error);
        }
        setAutomationLoading(false);
    };

    const handleGoogleImagesSearch = async () => {
        setGoogleImagesLoading(true);
        try {
            const response = await fetch(`${API_BASE}/facilities/find-google-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    facility_ids: selectedFacilities.length > 0 ? selectedFacilities : null
                })
            });

            const result = await response.json();

            if (result.success) {
                setNotification({
                    type: 'success',
                    message: `Successfully queued Google Images search for ${result.facility_count} facilities! Task ID: ${result.task_id}`
                });
                setSelectedFacilities([]);
            } else {
                setNotification({
                    type: 'error',
                    message: 'Failed to queue Google Images search tasks'
                });
            }
        } catch (err) {
            setNotification({
                type: 'error',
                message: `Failed to start Google Images search: ${err.message}`
            });
        } finally {
            setGoogleImagesLoading(false);
        }
    };

    const handleGoogleDataSearch = async () => {
        setGoogleDataLoading(true);
        try {
            const response = await fetch(`${API_BASE}/facilities/find-google-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    facility_ids: selectedFacilities.length > 0 ? selectedFacilities : null
                })
            });

            const result = await response.json();

            if (result.success) {
                setNotification({
                    type: 'success',
                    message: `Successfully queued Google Data search for ${result.facility_count} facilities! Task ID: ${result.task_id}`
                });
                setSelectedFacilities([]);
            } else {
                setNotification({
                    type: 'error',
                    message: 'Failed to queue Google Data search tasks'
                });
            }
        } catch (err) {
            setNotification({
                type: 'error',
                message: `Failed to start Google Data search: ${err.message}`
            });
        } finally {
            setGoogleDataLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" component="h1">
                        Facilities Manager
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh Data">
                        <IconButton onClick={apiActions.fetchFacilities} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh Analysis Data">
                        <IconButton onClick={fetchFacilityAnalysisSummaries} color="secondary">
                            <AnalyticsIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<ScrapeIcon />}
                        onClick={() => setScrapeDialog(true)}
                    >
                        Scrape Facilities
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ExportIcon />}
                        disabled={filteredFacilities.length === 0}
                    >
                        Export Data
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => setSendToKiddieDialog(true)}
                        color="success"
                    >
                        Send to Kiddie
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ReportIcon />}
                        onClick={() => setSendInspectionReportsToKiddieDialog(true)}
                        color="info"
                    >
                        Send Reports to Kiddie
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<KimiIcon />}
                        onClick={() => setKimiAnalysisDialog(true)}
                        color="info"
                    >
                        Kimi Analysis
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={handleGoogleImagesSearch}
                        disabled={googleImagesLoading}
                        color="secondary"
                    >
                        {googleImagesLoading ? 'Searching...' : 'Find Google Images'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<MapIcon />}
                        onClick={handleGoogleDataSearch}
                        disabled={googleDataLoading}
                        color="primary"
                    >
                        {googleDataLoading ? 'Searching...' : 'Find Google Data'}
                    </Button>
                    {/* Add toggles for continuous operations */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={autoScrapeEnabled}
                                onChange={(e) => handleAutoScrapeToggle(e.target.checked)}
                                color="primary"
                                disabled={automationLoading}
                            />
                        }
                        label="Auto Scrape"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={autoAnalysisEnabled}
                                onChange={(e) => handleAutoAnalysisToggle(e.target.checked)}
                                color="secondary"
                                disabled={automationLoading}
                            />
                        }
                        label="Auto Analysis"
                    />
                </Stack>
            </Box>

            {/* Analysis Activity Summary */}
            {Object.keys(analysisProgress).length > 0 && (
                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'info.main' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <AnalyticsIcon color="info" />
                            <Typography variant="h6" color="info.main">
                                Analysis Activity
                            </Typography>
                            <Chip
                                label={`${Object.keys(analysisProgress).length} running`}
                                color="info"
                                size="small"
                            />
                        </Box>
                        <Grid container spacing={2}>
                            {Object.entries(analysisProgress).slice(0, 4).map(([key, progress]) => {
                                const facilityId = key.split('_')[0];
                                const analysisType = key.split('_').pop();
                                const facility = facilities.find(f => f.id === facilityId);

                                return (
                                    <Grid item xs={12} sm={6} md={3} key={key}>
                                        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="subtitle2" noWrap>
                                                {facility?.name || facilityId}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                {analysisType === 'pricing' ? <PricingIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                                                <Typography variant="body2" color="text.secondary">
                                                    {analysisType}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progress.progress_percent}
                                                sx={{ mb: 1 }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {progress.current_step}: {progress.progress_percent}%
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                            {Object.keys(analysisProgress).length > 4 && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            +{Object.keys(analysisProgress).length - 4} more
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search facilities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="scraped">Scraped</MenuItem>
                                    <MenuItem value="not_scraped">Not Scraped</MenuItem>
                                    <MenuItem value="scraping_failed">Scraping Failed</MenuItem>
                                    <MenuItem value="url_analysis_failed">URL Analysis Failed</MenuItem>
                                    <MenuItem value="queued">Queued</MenuItem>
                                    <MenuItem value="processing">Processing</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Analysis</InputLabel>
                                <Select
                                    value={analysisFilter}
                                    label="Analysis"
                                    onChange={(e) => setAnalysisFilter(e.target.value)}
                                    disabled={analysisSummariesLoading}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="analyzed">
                                        Analyzed {(() => {
                                            if (analysisSummariesLoading) {
                                                return <CircularProgress size={16} sx={{ ml: 1 }} />;
                                            }
                                            // Count facilities that have completed analyses
                                            const analyzedCount = Object.values(facilityAnalysisSummaries).filter(summary =>
                                                summary && summary.analyses && Object.values(summary.analyses).some(analysis =>
                                                    analysis.available === true || analysis.status === 'completed'
                                                )
                                            ).length;

                                            if (analyzedCount > 0) {
                                                return `(${analyzedCount})`;
                                            }
                                            return '';
                                        })()}
                                    </MenuItem>
                                    <MenuItem value="not_analyzed">Not Analyzed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Typography variant="body2" color="text.secondary">
                                {filteredFacilities.length} of {facilities.length} facilities
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Bulk Actions */}
            {selectedFacilities.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography>
                            {selectedFacilities.length} facilities selected
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<ScrapeIcon />}
                                onClick={() => setScrapeDialog(true)}
                            >
                                Scrape Selected
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<AnalyticsIcon />}
                                onClick={() => setBulkAnalysisDialog(true)}
                                disabled={!facilities.filter(f => selectedFacilities.includes(f.id) && f.scraped_at).length}
                            >
                                Force Description Analysis
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<UploadIcon />}
                                onClick={() => setSendToKiddieDialog(true)}
                            >
                                Send to Kiddie
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="info"
                                startIcon={<ReportIcon />}
                                onClick={() => setSendInspectionReportsToKiddieDialog(true)}
                            >
                                Send Reports to Kiddie
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="info"
                                startIcon={<KimiIcon />}
                                onClick={handleKimiAnalysisFromSelection}
                            >
                                Kimi Analysis
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setSelectedFacilities([])}
                            >
                                Clear Selection
                            </Button>
                        </Stack>
                    </Box>
                </Alert>
            )}

            {/* Facilities Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedFacilities.length > 0 && selectedFacilities.length < paginatedFacilities.length}
                                    checked={paginatedFacilities.length > 0 && selectedFacilities.length === paginatedFacilities.length}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'id'}
                                    direction={orderBy === 'id' ? order : 'asc'}
                                    onClick={() => handleSort('id')}
                                >
                                    Facility ID
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'scraped_at'}
                                    direction={orderBy === 'scraped_at' ? order : 'asc'}
                                    onClick={() => handleSort('scraped_at')}
                                >
                                    Last Scraped
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Analyses</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'kimi_confidence'}
                                    direction={orderBy === 'kimi_confidence' ? order : 'asc'}
                                    onClick={() => handleSort('kimi_confidence')}
                                >
                                    Kimi Confidence
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {facilitiesLoading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : paginatedFacilities.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        No facilities found matching your criteria
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedFacilities.map((facility) => (
                                <TableRow key={facility.id} hover>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedFacilities.includes(facility.id)}
                                            onChange={() => handleSelectFacility(facility.id)}
                                        />
                                    </TableCell>
                                    <TableCell>{facility.id}</TableCell>
                                    <TableCell>{facility.name || 'Unknown'}</TableCell>
                                    <TableCell>{getStatusChip(facility)}</TableCell>
                                    <TableCell>
                                        {facility.scraped_at
                                            ? new Date(facility.scraped_at).toLocaleDateString()
                                            : 'Never'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            {getAnalysisStatusForFacility(facility.id) > 0 ? (
                                                <Typography variant="body2" color="info.main">
                                                    {getAnalysisStatusForFacility(facility.id)} running
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    None running
                                                </Typography>
                                            )}
                                            {(() => {
                                                const totalCount = getFacilityAnalysisCount(facility);
                                                const runningCount = getAnalysisStatusForFacility(facility.id);
                                                const completedCount = totalCount - runningCount;

                                                if (completedCount > 0) {
                                                    return (
                                                        <Typography variant="body2" color="success.main" sx={{ fontSize: '0.8rem' }}>
                                                            {completedCount} completed
                                                        </Typography>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const summary = facilityAnalysisSummaries[facility.id];
                                            const confidence = summary?.kimi_confidence;

                                            if (confidence !== undefined && confidence !== null) {
                                                return (
                                                    <Typography variant="body2" color={confidence > 0.7 ? "success.main" : confidence > 0.4 ? "warning.main" : "error.main"}>
                                                        {(confidence * 100).toFixed(1)}%
                                                    </Typography>
                                                );
                                            }
                                            return (
                                                <Typography variant="body2" color="text.secondary">
                                                    -
                                                </Typography>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(facility)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Run Analysis">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRunAnalysis(facility)}
                                                    disabled={!facility.scraped_at || (facilityAnalysisSummaries[facility.id] && facilityAnalysisSummaries[facility.id].website_content_empty)}
                                                >
                                                    <AnalyticsIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {facility.scraped_at && (
                                                <>
                                                    <Tooltip title="Quick Pricing Analysis (Ctrl+Click to force re-analysis)">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(event) => handleQuickAnalysis(facility, 'pricing', event)}
                                                            color="success"
                                                        >
                                                            <PricingIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Description Analysis - Optimized Content (Ctrl+Click to force re-analysis)">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(event) => handleQuickDescriptionAnalysis(facility, true, event)}
                                                            color="warning"
                                                            sx={{ fontSize: '0.75rem' }}
                                                        >
                                                            📝⚡
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Description Analysis - Full Content (Ctrl+Click to force re-analysis)">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(event) => handleQuickDescriptionAnalysis(facility, false, event)}
                                                            color="info"
                                                            sx={{ fontSize: '0.75rem' }}
                                                        >
                                                            📄🔍
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={filteredFacilities.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />

                {/* Background loading indicator */}
                {facilitiesLoading && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        py: 1,
                        backgroundColor: 'action.hover',
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                            Loading more facilities from Kiddie API...
                        </Typography>
                    </Box>
                )}
            </TableContainer>

            {/* Facility Details Dialog */}
            <Dialog
                open={showDetailsDialog}
                onClose={() => setDetailsDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Facility Details: {selectedFacility?.name || selectedFacility?.id}
                </DialogTitle>
                <DialogContent>
                    {facilityDetails ? (
                        <Box>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Basic Information</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">Facility ID:</Typography>
                                            <Typography>{facilityDetails.facility_id}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">Last Scraped:</Typography>
                                            <Typography>
                                                {facilityDetails.analysis_summary?.last_scraped
                                                    ? new Date(facilityDetails.analysis_summary.last_scraped).toLocaleString()
                                                    : 'Never'
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Analysis Results */}
                            {facilityDetails.analysis_results && Object.keys(facilityDetails.analysis_results).length > 0 && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6">Analysis Results</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {Object.entries(facilityDetails.analysis_results).map(([analysisType, analysisData]) => (
                                            <Box key={analysisType} sx={{ mb: 3 }}>
                                                <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
                                                    {
                                                        analysisType === 'description' ? '📝 Description Analysis' :
                                                            analysisType === 'pricing' ? '💰 Pricing Analysis' :
                                                                analysisType === 'kimi_analysis' ? '🤖 Kimi Analysis' :
                                                                    analysisType
                                                    }
                                                </Typography>

                                                {(analysisType === 'description' || analysisType === 'kimi_analysis') && analysisData.result && (
                                                    <Box>
                                                        {/* Description/NL for both */}
                                                        {analysisData.result.description_nl && (
                                                            <Paper sx={{ p: 2, mb: 2 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>📝 Description (NL)</Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                                    {analysisData.result.description_nl}
                                                                </Typography>
                                                            </Paper>
                                                        )}

                                                        {/* Kimi-specific fields */}
                                                        {analysisType === 'kimi_analysis' && (
                                                            <>
                                                                {analysisData.result.pricing_details && (
                                                                    <Accordion>
                                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Pricing Details</Typography></AccordionSummary>
                                                                        <AccordionDetails>
                                                                            <List>
                                                                                {analysisData.result.pricing_details.map((item, idx) => (
                                                                                    <ListItem key={idx}><ListItemText primary={`${item.type} - €${item.pricePerHour || item.price || '-'} per hour`} secondary={item.calculationBasis || item.notes} /></ListItem>
                                                                                ))}
                                                                            </List>
                                                                        </AccordionDetails>
                                                                    </Accordion>
                                                                )}
                                                                {analysisData.result.activities && (
                                                                    <Accordion>
                                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Activities</Typography></AccordionSummary>
                                                                        <AccordionDetails>
                                                                            {Object.entries(analysisData.result.activities).map(([k, v]) => <Chip key={k} label={k.replace(/_/g, ' ')} color={v ? 'success' : 'default'} sx={{ mr: 1, mb: 1 }} />)}
                                                                        </AccordionDetails>
                                                                    </Accordion>
                                                                )}
                                                                {analysisData.result.amenities && (
                                                                    <Accordion>
                                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Amenities</Typography></AccordionSummary>
                                                                        <AccordionDetails>
                                                                            {Object.entries(analysisData.result.amenities).map(([k, v]) => <Chip key={k} label={k.replace(/_/g, ' ')} color={v ? 'success' : 'default'} sx={{ mr: 1, mb: 1 }} />)}
                                                                        </AccordionDetails>
                                                                    </Accordion>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Description-specific fields */}
                                                        {analysisType === 'description' && analysisData.result.description_data && (
                                                            <Paper sx={{ p: 2, mb: 2 }}>
                                                                <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                                                    {JSON.stringify(analysisData.result.description_data, null, 2)}
                                                                </pre>
                                                            </Paper>
                                                        )}
                                                    </Box>
                                                )}

                                                {analysisType === 'pricing' && analysisData.result?.pricing_data && (
                                                    <Box>
                                                        <Typography variant="body1" color="text.secondary">
                                                            Pricing analysis results would be displayed here
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Analysis Summary</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                        {JSON.stringify(facilityDetails.analysis_summary, null, 2)}
                                    </pre>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Scraped Data</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '400px', overflow: 'auto' }}>
                                        {JSON.stringify(facilityDetails.scraped_data, null, 2)}
                                    </pre>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Website Pages & Relevance Analysis</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>AI-Powered Relevance Analysis:</strong> Each scraped page is analyzed by AI to determine its relevance for childcare information extraction.
                                            Pages marked as "Re-scrape needed" were scraped before this feature was enabled.
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Content Analysis:</strong> The first page with filtered content becomes the "main content" source for AI analysis, while remaining pages provide "background content" for additional context.
                                        </Typography>
                                    </Alert>

                                    {/* Category Summary */}
                                    {facilityDetails.scraped_data?.website_content?.categories && Object.keys(facilityDetails.scraped_data.website_content.categories).length > 0 && (
                                        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                Content Categories ({Object.values(facilityDetails.scraped_data.website_content.categories).reduce((sum, urls) => sum + urls.length, 0)} pages total)
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {Object.entries(facilityDetails.scraped_data.website_content.categories)
                                                    .sort(([, aUrls], [, bUrls]) => bUrls.length - aUrls.length) // Sort by page count descending
                                                    .map(([category, urls]) => (
                                                        <Grid item key={category}>
                                                            <Chip
                                                                label={`${category}: ${urls.length}`}
                                                                color={
                                                                    category === 'pricing' ? 'success' :
                                                                        category === 'activities' ? 'info' :
                                                                            category === 'contact' ? 'warning' :
                                                                                category === 'about' ? 'secondary' :
                                                                                    category === 'practical' ? 'primary' :
                                                                                        'default'
                                                                }
                                                                size="small"
                                                                sx={{
                                                                    textTransform: 'capitalize',
                                                                    fontWeight: urls.length > 0 ? 'bold' : 'normal'
                                                                }}
                                                            />
                                                        </Grid>
                                                    ))}
                                            </Grid>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Categories are automatically assigned based on URL patterns and content analysis. Higher page counts indicate more comprehensive coverage.
                                            </Typography>
                                        </Paper>
                                    )}

                                    {facilityDetails.scraped_data?.website_content?.pages ? (
                                        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>URL</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Category</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Main Content</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Relevant</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {Object.entries(facilityDetails.scraped_data.website_content.pages).map(([url, pageData], index) => {
                                                        // Determine category for this URL
                                                        const categories = facilityDetails.scraped_data.website_content.categories || {};
                                                        let pageCategory = 'uncategorized';
                                                        for (const [catName, catUrls] of Object.entries(categories)) {
                                                            if (catUrls.includes(url)) {
                                                                pageCategory = catName;
                                                                break;
                                                            }
                                                        }

                                                        // Check if this is main content (from Serper start URL)
                                                        const isMainContent = facilityDetails.scraped_data?.website_content?.start_url === url;

                                                        // Check relevance analysis
                                                        const relevanceAnalysis = pageData.relevance_analysis;

                                                        return (
                                                            <TableRow key={url} hover>
                                                                <TableCell sx={{ maxWidth: 200, wordBreak: 'break-all', fontSize: '0.75rem' }}>
                                                                    {url}
                                                                </TableCell>
                                                                <TableCell sx={{ maxWidth: 250 }}>
                                                                    {pageData.title || 'No title'}
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center' }}>
                                                                    <Chip
                                                                        label={pageCategory}
                                                                        color={
                                                                            pageCategory === 'pricing' ? 'success' :
                                                                                pageCategory === 'activities' ? 'info' :
                                                                                    pageCategory === 'contact' ? 'warning' :
                                                                                        pageCategory === 'about' ? 'secondary' :
                                                                                            'default'
                                                                        }
                                                                        size="small"
                                                                        sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center' }}>
                                                                    {isMainContent && (
                                                                        <Tooltip title="This page content was used as the main source for AI analysis (first page with filtered content)">
                                                                            <Chip
                                                                                label="MAIN"
                                                                                color="primary"
                                                                                size="small"
                                                                                sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center' }}>
                                                                    {pageData.relevance_analysis ? (
                                                                        <Chip
                                                                            label={pageData.relevance_analysis.is_relevant ? 'Yes' : 'No'}
                                                                            color={pageData.relevance_analysis.is_relevant ? 'success' : 'default'}
                                                                            size="small"
                                                                        />
                                                                    ) : (
                                                                        <Tooltip title="This facility was scraped before relevance analysis was enabled. Re-scrape to get relevance data.">
                                                                            <Chip label="Re-scrape needed" color="warning" size="small" />
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center' }}>
                                                                    {pageData.relevance_analysis?.reason || (
                                                                        <Box>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                No relevance analysis available
                                                                            </Typography>
                                                                            <Typography variant="caption" color="warning.main">
                                                                                Re-scrape facility to get AI-powered relevance analysis
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Typography color="text.secondary">
                                            No website data available
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Analysis Dialog */}
            <Dialog
                open={showAnalysisDialog}
                onClose={() => setAnalysisDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Run Analysis: {selectedFacility?.name || selectedFacility?.id}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Select Analysis Types:
                        </Typography>
                        {analysisTypes.map((type) => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={selectedAnalysisTypes.includes(type)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedAnalysisTypes([...selectedAnalysisTypes, type]);
                                            } else {
                                                setSelectedAnalysisTypes(
                                                    selectedAnalysisTypes.filter(t => t !== type)
                                                );
                                            }
                                        }}
                                    />
                                }
                                label={type.charAt(0).toUpperCase() + type.slice(1)}
                            />
                        ))}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={forceReanalysis}
                                    onChange={(e) => setForceReanalysis(e.target.checked)}
                                />
                            }
                            label="Force Re-analysis"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAnalysisDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleStartAnalysis}
                        disabled={selectedAnalysisTypes.length === 0 || analysisLoading}
                        variant="contained"
                    >
                        {analysisLoading ? <CircularProgress size={20} /> : 'Start Analysis'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Scrape Dialog */}
            <Dialog
                open={showScrapeDialog}
                onClose={() => setScrapeDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Scrape Facilities</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedFacilities.length > 0
                            ? `Scrape ${selectedFacilities.length} selected facilities?`
                            : 'Scrape all facilities?'
                        }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        This will start a background scraping task. You can monitor progress in the dashboard.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScrapeDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleBulkScrape}
                        disabled={scrapeLoading}
                        variant="contained"
                    >
                        {scrapeLoading ? <CircularProgress size={20} /> : 'Start Scraping'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Analysis Dialog */}
            <Dialog
                open={showBulkAnalysisDialog}
                onClose={() => setBulkAnalysisDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Force Description Analysis</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Run description analysis for {facilities.filter(f => selectedFacilities.includes(f.id) && f.scraped_at).length} selected facilities?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        This will force re-analysis of all selected facilities with scraped data.
                        This process may take several minutes.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkAnalysisDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleBulkFullAnalysis}
                        disabled={bulkAnalysisLoading}
                        variant="contained"
                        color="secondary"
                    >
                        {bulkAnalysisLoading ? <CircularProgress size={20} /> : 'Start Description Analysis'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send to Kiddie Dialog */}
            <Dialog
                open={showSendToKiddieDialog}
                onClose={() => setSendToKiddieDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Send Data to Kiddie API</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedFacilities.length > 0
                            ? `Create ${selectedFacilities.length} individual tasks for Kiddie API?`
                            : 'Create individual tasks for all analyzed facilities?'
                        }
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        This will create individual background tasks for each facility to send to Kiddie API.
                        Your 4 workers will process these tasks in parallel. Perfect for large batches!
                    </Typography>

                    {/* COMMENTED OUT - No pricing data
                    <FormControlLabel
                        control={
                            <Switch
                                checked={false}
                                onChange={(e) => {}}
                                color="success"
                            />
                        }
                        label="Include pricing data (separate API call)"
                    />
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Pricing data will be sent to a separate endpoint if available
                    </Typography>
                    */}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendToKiddieDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSendToKiddie}
                        disabled={sendToKiddieLoading}
                        variant="contained"
                        color="success"
                    >
                        {sendToKiddieLoading ? <CircularProgress size={20} /> : 'Create Tasks'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Inspection Reports to Kiddie Dialog */}
            <Dialog
                open={sendInspectionReportsToKiddieDialog}
                onClose={() => setSendInspectionReportsToKiddieDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Send Inspection Reports to Kiddie</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Send inspection report analysis data to Kiddie API for facilities with available inspection reports.
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedFacilities.length > 0 ? (
                            <>Will send inspection reports for <strong>{selectedFacilities.length}</strong> selected facilities.</>
                        ) : (
                            <>Will send inspection reports for <strong>all facilities</strong> that have inspection reports available.</>
                        )}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        Only facilities with processed inspection reports will be included in the submission.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendInspectionReportsToKiddieDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSendInspectionReportsToKiddie}
                        disabled={sendInspectionReportsToKiddieLoading}
                        variant="contained"
                        color="info"
                    >
                        {sendInspectionReportsToKiddieLoading ? <CircularProgress size={20} /> : 'Send Reports'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Kimi Analysis Dialog */}
            <Dialog
                open={showKimiAnalysisDialog}
                onClose={() => setKimiAnalysisDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Kimi Childcare Facility Analysis</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Analyze a childcare facility using Kimi Moonshot AI with web search and crawling capabilities.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Facility Name"
                                value={kimiFormData.facility_name}
                                onChange={(e) => handleKimiFormChange('facility_name', e.target.value)}
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={kimiFormData.facility_location}
                                onChange={(e) => handleKimiFormChange('facility_location', e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Facility Type</InputLabel>
                                <Select
                                    value={kimiFormData.facility_type}
                                    label="Facility Type"
                                    onChange={(e) => handleKimiFormChange('facility_type', e.target.value)}
                                >
                                    <MenuItem value="KDV">KDV (Kinderdagverblijf)</MenuItem>
                                    <MenuItem value="BSO">BSO (Buitenschoolse Opvang)</MenuItem>
                                    <MenuItem value="Peuteropvang">Peuteropvang</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Website (Optional)"
                                value={kimiFormData.website}
                                onChange={(e) => handleKimiFormChange('website', e.target.value)}
                                margin="normal"
                                placeholder="https://..."
                            />
                        </Grid>
                    </Grid>

                    {/* Analysis Result */}
                    {kimiAnalysisResult && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" gutterBottom>Analysis Result:</Typography>
                            <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                                    {JSON.stringify(kimiAnalysisResult, null, 2)}
                                </pre>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKimiAnalysisDialog(false)}>Close</Button>
                    <Button
                        onClick={handleKimiAnalysis}
                        disabled={kimiAnalysisLoading}
                        variant="contained"
                        color="info"
                    >
                        {kimiAnalysisLoading ? <CircularProgress size={20} /> : 'Analyze with Kimi'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            {notification && (
                <Snackbar
                    open={true}
                    autoHideDuration={6000}
                    onClose={() => setNotification(null)}
                >
                    <SnackbarContent
                        message={notification.message}
                        action={
                            notification.action && (
                                <Button color="inherit" size="small" onClick={notification.action.onClick}>
                                    {notification.action.label}
                                </Button>
                            )
                        }
                    />
                </Snackbar>
            )}
        </Box>
    );
};

export default FacilitiesManager; 